import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAnnouncement, closeAnnouncement } from "../announcement";
import type { Announcement, AnnouncementKind } from "../../pages/news/announcements";
import styles from "./Announce.module.scss";

// The start page's announcement (shared/announcement.ts): a quiet line on PC
// (its icon in the kind's colour, no box), a small pill on phones. With a
// link it opens it; × closes it for good.
const KIND_CLASS: Record<AnnouncementKind, string> = { android: styles.android, event: styles.event, info: styles.info };

function KindIcon({ kind }: { kind: AnnouncementKind }) {
  if (kind === "android") {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M17.6 9.48l1.84-3.18a.63.63 0 0 0-1.09-.63l-1.88 3.24a11.46 11.46 0 0 0-8.94 0L5.65 5.67a.64.64 0 0 0-1.09.63L6.4 9.48A10.78 10.78 0 0 0 1 18h22a10.78 10.78 0 0 0-5.4-8.52zM7 15.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm10 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z" /></svg>;
  }
  if (kind === "event") {
    return <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3.5" y="5" width="17" height="15.5" rx="2.5" /><path d="M3.5 10h17M8 3v4M16 3v4" /></svg>;
  }
  return <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" /></svg>;
}

function useOpen() {
  const navigate = useNavigate();
  return (link: string) => {
    if (/^https?:/.test(link)) window.open(link, "_blank", "noopener");
    else navigate(link);
  };
}

const kindOf = (a: Announcement): AnnouncementKind => a.kind ?? "info";

export function AnnounceBar() {
  const { shown } = useAnnouncement();
  const open = useOpen();
  if (!shown) return null;
  const kind = kindOf(shown);
  const body = (<>
    <b>{shown.title}</b>
    {shown.text && <span className={styles.text}>{shown.text}</span>}
    {shown.link && <span className={styles.more}>{shown.linkLabel ?? "Read more"} ›</span>}
  </>);
  return (
    <div className={`${styles.bar} ${KIND_CLASS[kind]}`} role="status">
      <span className={styles.icon}><KindIcon kind={kind} /></span>
      {shown.link
        ? <button type="button" className={styles.main} onClick={() => open(shown.link!)}>{body}</button>
        : <div className={styles.main}>{body}</div>}
      <button type="button" className={styles.close} onClick={() => closeAnnouncement(shown.id)} aria-label="Close">×</button>
    </div>
  );
}

export function AnnouncePill({ className }: { className?: string }) {
  const { shown } = useAnnouncement();
  const open = useOpen();
  if (!shown) return null;
  const kind = kindOf(shown);
  const inner = (<><span className={styles.icon}><KindIcon kind={kind} /></span><b>{shown.title}</b>{shown.link && <span className={styles.more}>›</span>}</>);
  return shown.link
    ? <button type="button" className={`${styles.pill} ${KIND_CLASS[kind]} ${className || ""}`} onClick={() => open(shown.link!)}>{inner}</button>
    : <div className={`${styles.pill} ${KIND_CLASS[kind]} ${className || ""}`} role="status">{inner}</div>;
}

// A game site's build (shared/portal-env.ts): where the announcement would be,
// the note new players get about our terms (CrazyGames asks for one when a game
// keeps more than their SDK does: names, rounds, error reports). Shown on the
// first visit only; × closes it sooner.
const NOTICE_KEY = "ar.portal.notice";
export function PortalNotice() {
  const [shown, setShown] = React.useState(() => {
    try { return !localStorage.getItem(NOTICE_KEY); } catch { return true; }
  });
  React.useEffect(() => {
    try { localStorage.setItem(NOTICE_KEY, "1"); } catch { /* no storage */ }
  }, []);
  if (!shown) return null;
  return (
    <div className={`${styles.pill} ${styles.notice} ${styles.info}`} role="status">
      <span className={styles.noticeText}>By playing you agree to our <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link></span>
      <button type="button" className={styles.noticeClose} onClick={() => setShown(false)} aria-label="Close">×</button>
    </div>
  );
}
